import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import firestoreService from '../services/firestoreService';
import AdminHeader from '../components/admin/AdminHeader';
import AdminNavigation from '../components/admin/AdminNavigation';
import './AuditLogs.css';

const AuditLogs = () => {
    const [auditLogs, setAuditLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [availableDataTypes, setAvailableDataTypes] = useState([]);
    const [groupByUser, setGroupByUser] = useState(false);
    const [filters, setFilters] = useState({
        search: '',
        severity: '',
        dataType: '',
        dateRange: '7d'
    });
    const [selectedLog, setSelectedLog] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedLogs, setSelectedLogs] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [logsPerPage] = useState(20);
    const [isDeleting, setIsDeleting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Check authentication
        const isAuthenticated = localStorage.getItem('adminAuth');
        if (!isAuthenticated) {
            navigate('/admin');
            return;
        }

        loadAuditLogs();
        loadAvailableDataTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    useEffect(() => {
        // Filter logs based on current filters
        let filtered = auditLogs;

        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(log =>
                log.userId.toLowerCase().includes(searchLower) ||
                log.action.toLowerCase().includes(searchLower) ||
                log.screenName?.toLowerCase().includes(searchLower) ||
                log.description?.toLowerCase().includes(searchLower)
            );
        }

        if (filters.severity) {
            filtered = filtered.filter(log => log.severity === filters.severity);
        }

        if (filters.dataType) {
            filtered = filtered.filter(log => log.dataType === filters.dataType);
        }

        if (filters.dateRange !== 'all') {
            const now = new Date();
            let startDate;

            switch (filters.dateRange) {
                case '1d':
                    startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                    break;
                case '7d':
                    startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                    break;
                case '30d':
                    startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                    break;
                default:
                    startDate = new Date(0);
            }

            filtered = filtered.filter(log => new Date(log.timestamp) >= startDate);
        }

        console.log('🔍 Filtering logs:', {
            original: auditLogs.length,
            filtered: filtered.length,
            filters: filters
        });
        setFilteredLogs(filtered);
    }, [auditLogs, filters]);

    const loadAuditLogs = async () => {
        try {
            setLoading(true);

            // Get date range
            const now = new Date();
            let startDate;

            switch (filters.dateRange) {
                case '1d':
                    startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                    break;
                case '7d':
                    startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                    break;
                case '30d':
                    startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                    break;
                default:
                    startDate = null;
            }

            console.log('Loading audit logs with filters:', {
                dataType: filters.dataType || 'all',
                severity: filters.severity || 'all',
                dateRange: filters.dateRange,
                startDate: startDate
            });

            const logsData = await firestoreService.getAuditLogs({
                dataType: filters.dataType || undefined,
                severity: filters.severity || undefined,
                startDate: startDate,
                endDate: filters.dateRange === 'all' ? null : now,
                limit: 1000  // Increased limit to get more logs
            });

            console.log('📊 Retrieved audit logs:', logsData.length);
            console.log('📋 Sample audit logs:', logsData.slice(0, 2));
            setAuditLogs(logsData || []);
        } catch (error) {
            console.error('Error loading audit logs:', error);
            setAuditLogs([]);
        } finally {
            setLoading(false);
        }
    };

    const loadAvailableDataTypes = async () => {
        try {
            const dataTypes = await firestoreService.getAvailableAuditDataTypes();
            console.log('Available data types:', dataTypes);
            setAvailableDataTypes(dataTypes);
        } catch (error) {
            console.error('Error loading available data types:', error);
            setAvailableDataTypes([]);
        }
    };

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
        setCurrentPage(1); // Reset to first page when filters change

        // Reload data when filters change
        if (filterName === 'dateRange' || filterName === 'dataType' || filterName === 'severity') {
            loadAuditLogs();
        }
    };

    const handleLogClick = (log) => {
        setSelectedLog(log);
        setShowModal(true);
    };

    const handleSelectLog = (log) => {
        setSelectedLogs(prev => {
            const isSelected = prev.some(l => l.id === log.id);
            if (isSelected) {
                return prev.filter(l => l.id !== log.id);
            } else {
                return [...prev, log];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectedLogs.length === currentLogs.length) {
            setSelectedLogs([]);
        } else {
            setSelectedLogs([...currentLogs]);
        }
    };

    const handleDeleteSelected = async () => {
        if (selectedLogs.length === 0) return;

        setIsDeleting(true);
        try {
            // Group logs by userId and dataType for efficient deletion
            const groupedLogs = {};
            selectedLogs.forEach(log => {
                const key = `${log.userId}_${log.dataType}`;
                if (!groupedLogs[key]) {
                    groupedLogs[key] = { userId: log.userId, dataType: log.dataType, logs: [] };
                }
                groupedLogs[key].logs.push({ id: log.id, docId: log.metadata?.docId || log.id });
            });

            let totalDeleted = 0;
            for (const key in groupedLogs) {
                const group = groupedLogs[key];
                const result = await firestoreService.deleteAuditLogs(
                    group.logs,
                    group.userId,
                    group.dataType
                );
                totalDeleted += result.deletedCount || 0;
            }

            if (totalDeleted > 0) {
                alert(`Successfully deleted ${totalDeleted} audit log(s)`);
                setSelectedLogs([]);
                loadAuditLogs();
            }
        } catch (error) {
            console.error('Error deleting audit logs:', error);
            alert('Failed to delete audit logs: ' + error.message);
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    };

    const formatTimestamp = (timestamp) => {
        if (timestamp && timestamp.toDate) {
            return timestamp.toDate().toLocaleString();
        }
        return new Date(timestamp).toLocaleString();
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'AuditLogSeverity.LOW':
            case 'LOW': return '#28a745';
            case 'AuditLogSeverity.MEDIUM':
            case 'MEDIUM': return '#ffc107';
            case 'AuditLogSeverity.HIGH':
            case 'HIGH': return '#dc3545';
            case 'AuditLogSeverity.CRITICAL':
            case 'CRITICAL': return '#6f42c1';
            default: return '#6c757d';
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'AuditLogType.FEATURE_ACCESS':
            case 'FEATURE_ACCESS': return '👁️';
            case 'AuditLogType.LINK_CLICKED':
            case 'LINK_CLICKED': return '🔗';
            case 'AuditLogType.BUTTON_CLICKED':
            case 'BUTTON_CLICKED': return '🔘';
            case 'AuditLogType.DATA_CREATE':
            case 'DATA_CREATE': return '➕';
            case 'AuditLogType.DATA_UPDATE':
            case 'DATA_UPDATE': return '✏️';
            case 'AuditLogType.DATA_DELETE':
            case 'DATA_DELETE': return '🗑️';
            case 'AuditLogType.ERROR_OCCURRED':
            case 'ERROR_OCCURRED': return '❌';
            case 'AuditLogType.APP_LAUNCH':
            case 'APP_LAUNCH': return '🚀';
            case 'DAILY_ACCESS': return '📊';
            case 'GENERAL': return '📝';
            default: return '📝';
        }
    };

    const groupLogsByUser = (logs) => {
        const grouped = {};
        logs.forEach(log => {
            if (!grouped[log.userId]) {
                grouped[log.userId] = [];
            }
            grouped[log.userId].push(log);
        });
        return grouped;
    };

    // Pagination
    const indexOfLastLog = currentPage * logsPerPage;
    const indexOfFirstLog = indexOfLastLog - logsPerPage;
    const currentLogs = filteredLogs.slice(indexOfFirstLog, indexOfLastLog);
    const totalPages = Math.ceil(filteredLogs.length / logsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    if (loading) {
        return (
            <div className="audit-logs">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading audit logs...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="audit-logs">
            <AdminHeader title="Audit Logs" />
            <AdminNavigation />

            <main className="admin-main">
                <div className="audit-logs-header">
                    <div className="filters-section">
                        <div className="filter-group">
                            <input
                                type="text"
                                placeholder="Search logs..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                className="search-input"
                            />
                        </div>

                        <div className="filter-group">
                            <select
                                value={filters.severity}
                                onChange={(e) => handleFilterChange('severity', e.target.value)}
                                className="filter-select"
                            >
                                <option value="">All Severities</option>
                                <option value="AuditLogSeverity.LOW">Low</option>
                                <option value="AuditLogSeverity.MEDIUM">Medium</option>
                                <option value="AuditLogSeverity.HIGH">High</option>
                                <option value="AuditLogSeverity.CRITICAL">Critical</option>
                            </select>
                        </div>

                        <div className="filter-group">
                            <select
                                value={filters.dataType}
                                onChange={(e) => handleFilterChange('dataType', e.target.value)}
                                className="filter-select"
                            >
                                <option value="">All Data Types</option>
                                {availableDataTypes.map(dataType => (
                                    <option key={dataType} value={dataType}>
                                        {dataType.replace('_', ' ').toUpperCase()}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <select
                                value={filters.dateRange}
                                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                                className="filter-select"
                            >
                                <option value="1d">Last 24 Hours</option>
                                <option value="7d">Last 7 Days</option>
                                <option value="30d">Last 30 Days</option>
                                <option value="all">All Time</option>
                            </select>
                        </div>
                    </div>

                    <div className="results-info">
                        <span>Showing {filteredLogs.length} of {auditLogs.length} logs</span>
                        <div className="group-toggle">
                            <label className="toggle-label">
                                <input
                                    type="checkbox"
                                    checked={groupByUser}
                                    onChange={(e) => setGroupByUser(e.target.checked)}
                                />
                                Group by User
                            </label>
                        </div>
                    </div>
                </div>

                <div className="audit-logs-table-container">
                    {selectedLogs.length > 0 && (
                        <div className="bulk-actions-bar">
                            <span>{selectedLogs.length} log(s) selected</span>
                            <button
                                className="bulk-delete-btn"
                                onClick={() => setShowDeleteModal(true)}
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete Selected'}
                            </button>
                        </div>
                    )}
                    {currentLogs.length > 0 ? (
                        groupByUser ? (
                            // Grouped by user display
                            <div className="user-groups">
                                {Object.entries(groupLogsByUser(currentLogs)).map(([userId, userLogs]) => (
                                    <div key={userId} className="user-group">
                                        <div className="user-group-header">
                                            <h3>User: {userId}</h3>
                                            <span className="log-count">{userLogs.length} logs</span>
                                        </div>
                                        <table className="audit-logs-table">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedLogs.length === userLogs.length && userLogs.length > 0}
                                                            onChange={handleSelectAll}
                                                        />
                                                    </th>
                                                    <th>Type</th>
                                                    <th>Action</th>
                                                    <th>Screen</th>
                                                    <th>Severity</th>
                                                    <th>Data Type</th>
                                                    <th>Timestamp</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {userLogs.map(log => (
                                                    <tr key={log.id}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedLogs.some(l => l.id === log.id)}
                                                                onChange={() => handleSelectLog(log)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <span className="type-icon">
                                                                {getTypeIcon(log.type)}
                                                            </span>
                                                        </td>
                                                        <td>{log.action}</td>
                                                        <td>{log.screenName || '-'}</td>
                                                        <td>
                                                            <span
                                                                className="severity-badge"
                                                                style={{ backgroundColor: getSeverityColor(log.severity) }}
                                                            >
                                                                {log.severity}
                                                            </span>
                                                        </td>
                                                        <td>{log.dataType}</td>
                                                        <td>{formatTimestamp(log.timestamp)}</td>
                                                        <td>
                                                            <button
                                                                className="view-details-btn"
                                                                onClick={() => handleLogClick(log)}
                                                            >
                                                                View Details
                                                            </button>
                                                            <button
                                                                className="delete-btn"
                                                                onClick={() => {
                                                                    setSelectedLogs([log]);
                                                                    handleDeleteSelected();
                                                                }}
                                                            >
                                                                🗑️
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            // Regular table display
                            <table className="audit-logs-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <input
                                                type="checkbox"
                                                checked={selectedLogs.length === currentLogs.length && currentLogs.length > 0}
                                                onChange={handleSelectAll}
                                            />
                                        </th>
                                        <th>Type</th>
                                        <th>User ID</th>
                                        <th>Action</th>
                                        <th>Screen</th>
                                        <th>Severity</th>
                                        <th>Data Type</th>
                                        <th>Timestamp</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentLogs.map(log => (
                                        <tr key={log.id}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLogs.some(l => l.id === log.id)}
                                                    onChange={() => handleSelectLog(log)}
                                                />
                                            </td>
                                            <td>
                                                <span className="type-icon">
                                                    {getTypeIcon(log.type)}
                                                </span>
                                            </td>
                                            <td>{log.userId}</td>
                                            <td>{log.action}</td>
                                            <td>{log.screenName || '-'}</td>
                                            <td>
                                                <span
                                                    className="severity-badge"
                                                    style={{ backgroundColor: getSeverityColor(log.severity) }}
                                                >
                                                    {log.severity}
                                                </span>
                                            </td>
                                            <td>{log.dataType}</td>
                                            <td>{formatTimestamp(log.timestamp)}</td>
                                            <td>
                                                <button
                                                    className="view-details-btn"
                                                    onClick={() => handleLogClick(log)}
                                                >
                                                    View Details
                                                </button>
                                                <button
                                                    className="delete-btn"
                                                    onClick={() => {
                                                        setSelectedLogs([log]);
                                                        handleDeleteSelected();
                                                    }}
                                                >
                                                    🗑️
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )
                    ) : (
                        <div className="no-logs-message">
                            <div className="no-logs-icon">📋</div>
                            <h3>No Audit Logs Found</h3>
                            <p>No audit logs match your current filters.</p>
                            <p>Try adjusting your search criteria or date range.</p>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="pagination">
                        <button
                            className="pagination-btn"
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                            <button
                                key={number}
                                className={`pagination-btn ${currentPage === number ? 'active' : ''}`}
                                onClick={() => paginate(number)}
                            >
                                {number}
                            </button>
                        ))}

                        <button
                            className="pagination-btn"
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                    </div>
                )}

                {showModal && selectedLog && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h2>Audit Log Details</h2>
                                <button
                                    className="close-button"
                                    onClick={() => setShowModal(false)}
                                >
                                    ×
                                </button>
                            </div>

                            <div className="modal-body">
                                <div className="log-details">
                                    <div className="detail-row">
                                        <label>Log ID:</label>
                                        <span>{selectedLog.id}</span>
                                    </div>

                                    <div className="detail-row">
                                        <label>User ID:</label>
                                        <span>{selectedLog.userId}</span>
                                    </div>

                                    <div className="detail-row">
                                        <label>Type:</label>
                                        <span>{selectedLog.type}</span>
                                    </div>

                                    <div className="detail-row">
                                        <label>Action:</label>
                                        <span>{selectedLog.action}</span>
                                    </div>

                                    <div className="detail-row">
                                        <label>Description:</label>
                                        <span>{selectedLog.description}</span>
                                    </div>

                                    <div className="detail-row">
                                        <label>Screen:</label>
                                        <span>{selectedLog.screenName || 'N/A'}</span>
                                    </div>

                                    <div className="detail-row">
                                        <label>Severity:</label>
                                        <span
                                            className="severity-badge"
                                            style={{ backgroundColor: getSeverityColor(selectedLog.severity) }}
                                        >
                                            {selectedLog.severity}
                                        </span>
                                    </div>

                                    <div className="detail-row">
                                        <label>Data Type:</label>
                                        <span>{selectedLog.dataType}</span>
                                    </div>

                                    <div className="detail-row">
                                        <label>Timestamp:</label>
                                        <span>{formatTimestamp(selectedLog.timestamp)}</span>
                                    </div>

                                    <div className="detail-row">
                                        <label>Metadata:</label>
                                        <pre className="metadata-display">
                                            {JSON.stringify(selectedLog.metadata, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    className="close-button"
                                    onClick={() => setShowModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteModal && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h2>Confirm Delete</h2>
                                <button
                                    className="close-button"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    ×
                                </button>
                            </div>

                            <div className="modal-body">
                                <p>Are you sure you want to delete {selectedLogs.length} audit log(s)?</p>
                                <p className="warning-text">This action cannot be undone.</p>
                            </div>

                            <div className="modal-footer">
                                <button
                                    className="cancel-button"
                                    onClick={() => setShowDeleteModal(false)}
                                    disabled={isDeleting}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="confirm-delete-button"
                                    onClick={handleDeleteSelected}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default AuditLogs;
